import type { FileEntry } from '@/lib/api/generated/model';
import { classifyFileEntry } from './fileClassification';
import type { SortDirection, SortKey } from './types';

/**
 * Folders rank above files, always (spec 059 FR-006).
 *
 * This is an invariant, not a preference: it is not exposed as a setting and no
 * sort direction can invert it. Expressing it as the comparator's first term
 * makes that structural — a sorted listing cannot interleave the two, because
 * the comparator cannot produce that ordering.
 */
function kindRank(entry: FileEntry): number {
  return entry.kind === 'directory' ? 0 : 1;
}

/**
 * Ordinal (not locale-aware) comparison, used only as the tiebreak. The
 * primary keys below use `localeCompare`/numeric comparison so the visible
 * order matches user expectations, but the tiebreak must be stable across
 * repeated sorts of the same listing (research.md R11) — a locale collator
 * can treat two distinct paths as equal (e.g. case- or accent-insensitive),
 * which would let the tiebreak itself reshuffle ties from one sort to the
 * next.
 */
function comparePaths(a: FileEntry, b: FileEntry): number {
  if (a.path < b.path) return -1;
  if (a.path > b.path) return 1;
  return 0;
}

function compareByKey(
  a: FileEntry,
  b: FileEntry,
  key: SortKey,
  labels: Map<FileEntry, string>,
): number {
  switch (key) {
    case 'name':
      return a.name.localeCompare(b.name);
    case 'kind':
      // The displayed label, not the `directory`/`file` discriminator. Once
      // folders are pinned above files by `kindRank`, comparing the
      // discriminator would make every file equal to every other one — a column
      // that looks sortable and does nothing (research R3).
      //
      // Read from a table built once per sort. Classifying inside the
      // comparator meant roughly n·log n classifications for a listing of n
      // entries, when n of them will do.
      return (labels.get(a) ?? '').localeCompare(labels.get(b) ?? '');
    case 'size':
      return (a.size ?? 0) - (b.size ?? 0);
    case 'modified_at':
      return (a.modified_at ?? '').localeCompare(b.modified_at ?? '');
  }
}

/**
 * Sorts a directory listing by `key`/`direction`, with folders always ahead of
 * files and a stable, case-sensitive `path` tiebreak so entries with equal
 * primary-key values never reorder between sorts (research.md R11).
 */
export function sortFileEntries(
  entries: FileEntry[],
  key: SortKey,
  direction: SortDirection,
): FileEntry[] {
  const sign = direction === 'asc' ? 1 : -1;
  // Only the kind column needs labels, so nothing is classified for the others.
  const labels =
    key === 'kind'
      ? new Map(entries.map((entry) => [entry, classifyFileEntry(entry).label]))
      : new Map<FileEntry, string>();
  return [...entries].sort(
    // Neither the folder rank nor the tiebreak is multiplied by `sign`.
    //
    // The rank leads because folders precede files under every column and both
    // directions; flipping it would send folders to the bottom on a second
    // header click, hiding navigation targets at the end of a long list.
    //
    // The tiebreak trails for the same reason it always has: it exists only to
    // make ties deterministic, and flipping it would make the tiebreak itself
    // reorder entries when the reader merely reverses the primary sort.
    (a, b) =>
      kindRank(a) - kindRank(b) ||
      sign * compareByKey(a, b, key, labels) ||
      comparePaths(a, b),
  );
}
